/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React from 'react';
import { withStyles } from '@mui/styles';
import { FormattedMessage, FormattedDate } from 'react-intl';
import logo from '../../images/logo.png';
import { Grid2 as Grid, Box } from '@mui/material';
import { locale } from '../../intl';
import { resolveLanguage } from '../../../config/i18n/';
import { StyledSmallLink } from '../../OCLCMaterialTheme/commonStyles';

const styles = {
  logo: {
    verticalAlign: 'middle',
    display: 'inline-block',
    height: '52px',
    width: '160px',
  },
};

const chineseLocale = {
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
};

const copyRight = '\u00A9';

const Footer = React.memo(({ classes }) => {
  const language = chineseLocale[locale]
    ? chineseLocale[locale]
    : resolveLanguage(locale);

  return (
    <Box
      component='footer'
      sx={{
        backgroundColor: 'common.white',
        padding: 4,
      }}
    >
      <Grid
        container
        spacing={4}
        sx={{
          flexDirection: { xs: 'column', sm: 'row' }, // Rows on mobile, columns on desktop
          alignItems: { xs: 'normal', sm: 'center' },
          justifyContent: { xs: 'normal', md: 'center' },
        }}
      >
        <Grid sx={{ padding: '0 12px' }}>
          <FormattedMessage id='Logo.altText' defaultMessage='OCLC logo'>
            {(altText) => (
              <img className={classes.logo} alt={altText} src={logo} />
            )}
          </FormattedMessage>
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: { sm: '520px' },
          }}
        >
          <StyledSmallLink
            data-testid='footer-copyright'
            href={`https://policies.oclc.org/${language}/copyright.html`}
            target='_blank'
            rel='noreferrer'
          >
            <FormattedDate value={new Date()} year={'numeric'}>
              {(year) => <span>{`${copyRight} ${year} OCLC`}</span>}
            </FormattedDate>
          </StyledSmallLink>

          <FormattedMessage
            id='Footer.message'
            defaultMessage='Domestic and international trademarks and/or service marks of OCLC, Inc. and its affiliates.'
          >
            {(label) => (
              <StyledSmallLink
                data-testid='footer-trademark'
                href={`https://policies.oclc.org/${language}/copyright/trademarks.html`}
                target='_blank'
                rel='noreferrer'
                aria-label={label}
              >
                {label}
              </StyledSmallLink>
            )}
          </FormattedMessage>

          <FormattedMessage
            id='Footer.legalNotices'
            defaultMessage='Legal notices'
          >
            {(label) => (
              <StyledSmallLink
                data-testid='footer-legal-notice'
                href={`https://policies.oclc.org/${language}/home.html`}
                target='_blank'
                rel='noreferrer'
                aria-label={label}
              >
                {label}
              </StyledSmallLink>
            )}
          </FormattedMessage>
        </Grid>
      </Grid>
    </Box>
  );
});

export default withStyles(styles)(Footer);
